import { useMemo, useRef, useState, type FormEvent } from 'react';
import { services } from '@/mocks/services';
import { usePublicPricing } from '@/lib/pricing';
import PriceNote from '@/components/feature/PriceNote';
import { Field, inputClass } from './fields';
import PricingSummary from './PricingSummary';
import { BOOKING_FORM_URL, bookingAvailable } from '@/config/booking';

// Sends to native Forms only. Success requires HTTP 2xx AND code === 'OK'.
async function submitToForms(fd: FormData): Promise<void> {
  const body = new URLSearchParams();
  fd.forEach((val, key) => {
    if (typeof val !== 'string') return;
    if (key === 'company_alt' && !val.trim()) return;
    body.append(key, val.trim());
  });
  const response = await fetch(BOOKING_FORM_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  const responseText = await response.text();
  let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: unknown } } | null = null;
  try { parsed = JSON.parse(responseText); } catch { parsed = null; }
  const detail = typeof parsed?.meta?.detail === 'string' ? parsed.meta.detail : '';
  const serverMsg = parsed?.meta?.message || parsed?.message || detail || '';
  const looksSpam = /spam/i.test(`${serverMsg} ${detail} ${responseText}`);
  if (response.ok && parsed?.code === 'OK' && !looksSpam) return;
  if (looksSpam) throw new Error('Your request was flagged by our spam filter and was not saved. Please review your details and try again, or contact us by phone.');
  throw new Error(serverMsg ? `We could not save your request (${serverMsg}). Please try again.` : 'We could not save your request. Please try again.');
}

const stepLabels = ['Appliance', 'Repair Details', 'Preferred Appointment', 'Contact & Review'];
const windows = [
  { id: 'Morning', note: 'Approx. 8am – 12pm' },
  { id: 'Afternoon', note: 'Approx. 12pm – 4pm' },
  { id: 'Evening', note: 'Approx. 4pm – 7pm' },
];

interface Values {
  appliance: string; issue: string; address: string; date: string; window: string; name: string; email: string; phone: string;
}

interface BookingFormProps {
  initialAppliance?: string;
}

export default function BookingForm({ initialAppliance = '' }: BookingFormProps) {
  const initialName = services.find((s) => s.slug === initialAppliance)?.name ?? '';
  const [step, setStep] = useState(0);
  const [v, setV] = useState<Values>({ appliance: initialName, issue: '', address: '', date: '', window: '', name: '', email: '', phone: '' });
  const [errors, setErrors] = useState<Partial<Record<keyof Values, string>>>({});
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const submitLock = useRef(false);
  const pricing = usePublicPricing();

  const selectedSlug = services.find((s) => s.name === v.appliance)?.slug ?? '';
  const topRef = useRef<HTMLFormElement>(null);
  // Local calendar date (toISOString is UTC and can shift the day).
  const today = useMemo(() => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }, []);

  const goTo = (s: number) => {
    setStep(s);
    if (status === 'error') { setStatus('idle'); setFormError(''); }
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const set = (k: keyof Values, val: string) => { setV((p) => ({ ...p, [k]: val })); setErrors((p) => ({ ...p, [k]: undefined })); };

  const validate = (s: number) => {
    const e: Partial<Record<keyof Values, string>> = {};
    if (s === 0 && !v.appliance) e.appliance = 'Please choose an appliance.';
    if (s === 1) {
      if (v.issue.trim().length < 10) e.issue = 'Please describe the issue (at least 10 characters).';
      if (v.issue.length > 500) e.issue = 'Please keep it under 500 characters.';
      if (v.address.trim().length < 6) e.address = 'Please enter the full service address.';
    }
    if (s === 2) {
      if (!v.date) e.date = 'Please choose a preferred date.';
      else if (v.date < today) e.date = 'Please choose today or a later date.';
      if (!v.window) e.window = 'Please choose a time window.';
    }
    if (s === 3) {
      if (v.name.trim().length < 2) e.name = 'Please enter your name.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email)) e.email = 'Please enter a valid email.';
      if (v.phone.replace(/\D/g, '').length < 7) e.phone = 'Please enter a valid phone number.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validate(step)) goTo(step + 1); };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (step < 3) { next(); return; }
    const fd = new FormData(event.currentTarget);
    const trap = String(fd.get('company_alt') ?? '').trim();
    if (trap) { setStatus('success'); return; }
    if (submitLock.current || status === 'sending') return;
    if (!validate(3)) return;
    // Re-check earlier steps too, in case values changed after navigating back.
    if (![0, 1, 2].every((s) => validate(s))) { setStatus('error'); setFormError('Some details are missing or invalid. Please review each step.'); return; }
    if (!bookingAvailable) { setStatus('error'); setFormError('Online booking has not been set up yet. Please contact us by phone or email instead.'); return; }
    submitLock.current = true;
    setStatus('sending');
    setFormError('');
    try {
      await submitToForms(fd);
      setStatus('success');
      return; // lock stays engaged so the same request can't be re-sent
    } catch (err) {
      setStatus('error');
      setFormError(err instanceof Error ? err.message : 'We could not save your request. Please try again.');
    }
    submitLock.current = false;
  };

  if (status === 'success') {
    return (
      <div className="rounded-xl border border-background-200 bg-background-50 p-8 md:p-12">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-700"><i className="ri-check-line text-xl" /></div>
        <h2 className="mt-6 text-2xl md:text-3xl font-medium tracking-tight text-foreground-950">Request submitted.</h2>
        <p className="mt-3 max-w-md text-[15px] leading-relaxed text-foreground-600">
          Your repair request has been submitted. This is not a confirmed appointment and no technician has been assigned yet: we&apos;ll review it and contact you to confirm an exact time.
        </p>
      </div>
    );
  }

  const choice = (selected: boolean) => `cursor-pointer rounded-md border px-4 py-4 text-left transition-colors ${selected ? 'border-primary-500 bg-primary-50 text-foreground-950' : 'border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300'}`;

  return (
    <form ref={topRef} id="mendwell-booking" data-readdy-form onSubmit={handleSubmit} noValidate className="relative rounded-xl border border-background-200 bg-background-50 p-5 md:p-10">
      <input type="text" name="company_alt" className="mw-field-aux" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly defaultValue="" />
      <input type="hidden" name="appliance" value={v.appliance} />
      <input type="hidden" name="time_window" value={v.window} />

      <div className="mb-8 flex items-center gap-2">
        {stepLabels.map((l, i) => (
          <span key={l} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? 'bg-primary-500' : 'bg-background-200'}`} />
        ))}
      </div>
      <p className="text-xs uppercase tracking-[0.14em] text-foreground-600">{`Step 0${step + 1} / 04`}</p>
      <h2 className="mt-2 text-2xl md:text-[30px] font-medium tracking-tight text-foreground-950">{stepLabels[step]}</h2>

      <div className={step === 0 ? 'mt-8' : 'hidden'}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {services.map((s) => (
            <button key={s.slug} type="button" onClick={() => set('appliance', s.name)} className={choice(v.appliance === s.name)}>
              <span className="block text-[15px]">{s.name}</span>
              <span className="mt-1 block text-xs text-foreground-600">{s.summary}</span>
            </button>
          ))}
        </div>
        {errors.appliance && <p className="mt-3 text-xs text-primary-700">{errors.appliance}</p>}
        {selectedSlug && <PriceNote pricing={pricing} slug={selectedSlug} className="mt-6" />}
      </div>

      <div className={step === 1 ? 'mt-8 space-y-5' : 'hidden'}>
        <Field label="What's wrong?" error={errors.issue} hint={`${v.issue.length}/500 characters`}>
          <textarea name="issue" rows={5} maxLength={500} value={v.issue} onChange={(e) => set('issue', e.target.value)} placeholder="e.g. The washer fills with water but the drum won't spin." className={`${inputClass} resize-none`} />
        </Field>
        <Field label="Service address" error={errors.address}>
          <input name="address" type="text" value={v.address} onChange={(e) => set('address', e.target.value)} placeholder="Street, city, postal code" className={inputClass} autoComplete="street-address" />
        </Field>
      </div>

      <div className={step === 2 ? 'mt-8 space-y-6' : 'hidden'}>
        <Field label="Preferred date" error={errors.date}>
          <input name="preferred_date" type="date" min={today} value={v.date} onChange={(e) => set('date', e.target.value)} className={inputClass} />
        </Field>
        <div>
          <span className="mb-2 block text-xs font-medium uppercase tracking-[0.1em] text-foreground-700">Preferred time window</span>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {windows.map((w) => (
              <button key={w.id} type="button" onClick={() => set('window', w.id)} className={choice(v.window === w.id)}>
                <span className="block text-[15px]">{w.id}</span>
                <span className="mt-1 block text-xs text-foreground-600">{w.note}</span>
              </button>
            ))}
          </div>
          {errors.window && <p className="mt-2 text-xs text-primary-700">{errors.window}</p>}
        </div>
        <p className="text-xs leading-relaxed text-foreground-600">These are preferences, not live availability. We&apos;ll confirm the exact time with you.</p>
      </div>

      <div className={step === 3 ? 'mt-8 space-y-5' : 'hidden'}>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Field label="Full name" error={errors.name}>
            <input name="name" type="text" value={v.name} onChange={(e) => set('name', e.target.value)} className={inputClass} autoComplete="name" />
          </Field>
          <Field label="Phone" error={errors.phone}>
            <input name="phone" type="tel" value={v.phone} onChange={(e) => set('phone', e.target.value)} className={inputClass} autoComplete="tel" />
          </Field>
        </div>
        <Field label="Email" error={errors.email}>
          <input name="email" type="email" value={v.email} onChange={(e) => set('email', e.target.value)} className={inputClass} autoComplete="email" />
        </Field>
        <dl className="mt-4 rounded-lg bg-background-100 p-5 text-sm">
          {([['Appliance', v.appliance, 0], ['Issue', v.issue, 1], ['Address', v.address, 1], ['Preferred', `${v.date} · ${v.window} (preference, to be confirmed)`, 2]] as [string, string, number][]).map(([k, val, s]) => (
            <div key={k} className="grid grid-cols-[88px_1fr_auto] gap-3 border-b border-background-200 py-2.5 last:border-0">
              <dt className="text-foreground-600">{k}</dt>
              <dd className="min-w-0 break-words text-foreground-950">{val}</dd>
              <button type="button" onClick={() => goTo(s)} className="cursor-pointer whitespace-nowrap text-xs text-primary-700 underline-offset-2 hover:underline">Edit</button>
            </div>
          ))}
        </dl>
        {selectedSlug && <PricingSummary pricing={pricing} slug={selectedSlug} serviceName={v.appliance} />}
        {!bookingAvailable && (
          <p className="rounded-md bg-primary-50 px-4 py-3 text-sm text-primary-800">Online booking has not been set up yet, so this request can&apos;t be sent. Please contact us by phone or email instead.</p>
        )}
        {status === 'error' && (
          <p className="rounded-md bg-primary-50 px-4 py-3 text-sm text-primary-800">
            {formError}{' '}<span className="whitespace-nowrap">Your details are kept, so you can press Submit Request again to retry.</span>
          </p>
        )}
      </div>

      <div className="mt-10 flex flex-col-reverse gap-3 border-t border-background-200 pt-6 sm:flex-row sm:justify-between">
        <div className="flex flex-col-reverse gap-3 sm:flex-row">
          <button type="button" onClick={() => goTo(step - 1)} disabled={step === 0 || status === 'sending'} className="h-12 whitespace-nowrap rounded-full border border-background-200 px-6 text-sm text-foreground-950 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40 hover:bg-background-100">
            Back
          </button>
        </div>
        {step < 3 ? (
          <button type="button" onClick={next} className="h-12 whitespace-nowrap rounded-full bg-primary-500 px-7 text-sm text-white cursor-pointer hover:bg-primary-600">Continue</button>
        ) : (
          <button type="submit" disabled={status === 'sending' || !bookingAvailable} className="h-12 whitespace-nowrap rounded-full bg-primary-500 px-7 text-sm text-white cursor-pointer hover:bg-primary-600 disabled:opacity-60">
            {status === 'sending' ? 'Sending…' : 'Submit Request'}
          </button>
        )}
      </div>
    </form>
  );
}